import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, Loader2, AlertCircle, ArrowUpRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { heroImages } from '../mock/data';

const Signup = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { register, user } = useAuth();

  if (user) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      await register(payload);
      nav('/account');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Registration failed. Please try again.');
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-[calc(100vh-200px)] grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={heroImages.warehouse} alt="Lanka Express" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/70 to-black/40" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Join Us</span></div>
          <h2 className="font-serif-display text-5xl font-bold leading-[1.05] mb-4">Create your<br /><em className="text-[#c8102e] not-italic">Lanka Express</em> account.</h2>
          <p className="text-white/70 max-w-md">Get priority support, save your favourite branches and stay updated on exclusive member offers.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-14 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-3"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Sign Up</span></div>
          <h1 className="font-serif-display text-4xl font-bold text-black mb-8">Create your account</h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Full Name</label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input required value={form.name} onChange={set('name')} placeholder="e.g. Nayana Kumari" className="pl-11 h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input type="email" required value={form.email} onChange={set('email')} placeholder="you@example.com" className="pl-11 h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Phone <span className="normal-case tracking-normal text-neutral-400">(optional)</span></label>
              <div className="relative">
                <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input value={form.phone} onChange={set('phone')} placeholder="+39 380 000 0000" className="pl-11 h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Password <span className="normal-case tracking-normal text-neutral-400">(6+ characters)</span></label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input type="password" required minLength={6} value={form.password} onChange={set('password')} placeholder="••••••••" className="pl-11 h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
              </div>
            </div>
            {err && <div className="flex items-center gap-2 text-sm text-[#c8102e]"><AlertCircle className="w-4 h-4" /> {err}</div>}
            <Button type="submit" disabled={busy} className="bg-[#c8102e] hover:bg-black text-white h-12 w-full font-semibold tracking-[0.15em] uppercase text-[13px]">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Create Account
            </Button>
          </form>
          <p className="mt-8 text-sm text-neutral-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#c8102e] font-semibold hover:underline inline-flex items-center gap-1">Sign in <ArrowUpRight className="w-3 h-3" /></Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
